/* eslint-disable @typescript-eslint/no-explicit-any */
/** import package [start] */
import { useEffect, useState, createRef, useImperativeHandle, forwardRef } from 'react';
import { Button } from '@zeeve-platform/button-component';
import { Modal } from '@zeeve-platform/modal-component';
import { Inputbox } from '@zeeve-platform/inputbox-component';
/** import package [end] */

/** import custom dependency [start] */
import { EndointDetailsTypes } from '../../../types';
import Loader from '../../../components/Loader';
import {
    updateEndpointName,
    getEndpointDetailsData,
    updateSecurity,
    getEndpointConsumed,
} from '../../../services/endpoint';
import BaseUrls from '../../../utils/baseUrls';
import { copyToClipboard, addBorderColor, removeBorderColorOnTimeout } from '../../../utils/helpers';
import JWTSecurity, { IFRef as JWTIFRef } from './Auth/JWT';
/** import custom dependency [end] */

type codeSnippetType = 'CURL' | 'WSCAT' | 'NodeJs' | 'Python' | 'Go' | 'none';

export interface IFRef {
    getEndpointName: () => string;
}

/** type declaration for Basic Details props */
export interface BasicProps {
    endpointId?: string;
    setupEndpointName: React.Dispatch<React.SetStateAction<string>>;
}

/**
 * End-Point Basic details component
 * @returns JSX.Element
 */

const Basic = forwardRef<IFRef, BasicProps>(({ endpointId, setupEndpointName }, ref) => {
    //misc states
    const [isLoading, setLoading] = useState<boolean>(true);
    const [isCopy, setCopy] = useState<boolean>(false);
    const [componentError, setComponentError] = useState<string | null>(null);

    //component dependent states
    const [isNameEditMode, toggleNameEditMode] = useState<boolean>(false);
    const [isEPNameSavebtnDisabled, toggleEPNameSavebtnDisable] = useState<boolean>(false);
    const [isSecurityEditMode, toggleSecurityEditMode] = useState<boolean>(false);
    const [endpointData, setEndpointData] = useState<EndointDetailsTypes>({} as EndointDetailsTypes);
    const [endpointConsumed, setEndpointConsumed] = useState<string>('0');
    const [endpointQuota, setEndpointQuota] = useState<string>('0');
    const [endpointName, setEndpointName] = useState<string>('');
    const [endpointNameError, setEndpointNameError] = useState<string | null>(null);
    const [isSecurity, toggleSecurity] = useState<boolean>(false);
    const [securityType, setSecurityType] = useState<'JWT' | 'none'>('none');
    const [securityError, setSecurityError] = useState<string | null>(null);
    const [endpointURLs, setEndpointURLs] = useState<{ http: string; wss: string }>({
        http: `${BaseUrls.getEndpointHttpUrl()}/${endpointData.apiKey}/`,
        wss: `${BaseUrls.getEndpointWssUrl()}/${endpointData.apiKey}/`,
    });
    const [codeSnippet, setCodeSnippet] = useState<codeSnippetType>('CURL');
    const [codeType, setCodeType] = useState<'https' | 'wss'>('https');
    const [codes, setCodes] = useState<{ lang: codeSnippetType; http?: string; wss?: string }[]>([]);

    // JWT
    const [jwtName, setJWTName] = useState<string>('');
    const [jwtKey, setJWTKey] = useState<string>('');

    //refs
    const endpointNameInputRef = createRef<HTMLInputElement>();
    const jwtSecurityRef = createRef<JWTIFRef>();

    //modal
    const [showModal, toggleModal] = useState<boolean>(false);
    const [modalTitle, setModalTitle] = useState<string>('');
    const [modalMessage, setModalMessage] = useState<string>('');
    const [modalConfirmText] = useState<string>('OK');
    const [modalVisibleConfirmBtn, toggleConfirmVisibility] = useState<boolean>(false);
    const [modalVisibleCancelBtn] = useState<boolean>(true);

    //exposed states and functions
    useImperativeHandle(ref, () => {
        return {
            getEndpointName: () => endpointName,
        };
    });

    const setupSecurity = (endpoint: EndointDetailsTypes): void => {
        if ('securityEnabled' in endpoint && endpoint.securityEnabled !== undefined) {
            toggleSecurity(endpoint.securityEnabled);
        } else {
            toggleSecurity(false);
            setSecurityType('none');
            return;
        }

        if ('jwtEnabled' in endpoint && endpoint.jwtEnabled !== undefined && endpoint.jwtEnabled === true) {
            setSecurityType('JWT');

            setJWTName(endpoint.jwtPublicName || '');
            setJWTKey(endpoint.jwtPublicKey || '');
        } else {
            setJWTName('');
            setJWTKey('');

            setSecurityType('none');
            toggleSecurity(false);
        }
    };

    /**
     * To reset the endpoint name to previous state
     */
    const resetEndpointName = (): void => {
        setEndpointName(endpointData.endpointName);
        toggleNameEditMode(false);
    };

    /**
     * To reset the endpoint security to previous state
     */
    const resetSecurity = (): void => {
        setupSecurity(endpointData);
        toggleSecurityEditMode(false);
    };

    /**
     * To Copy content to the clipboard
     * @param value value to be copied
     * @returns void
     */
    const copyText = (value: string): void => {
        copyToClipboard(value);
        setCopy(true);
        setTimeout(() => {
            setCopy(false);
        }, 1500);
    };

    /**
     * to validate the endpoint name
     * @returns void
     */
    const validateEndpointName = (): void => {
        toggleEPNameSavebtnDisable(false);
        if (isLoading) return;

        if (!isNameEditMode) {
            addBorderColor(endpointNameInputRef);
            setEndpointNameError(null);

            return;
        }

        addBorderColor(endpointNameInputRef, 'red');
        setEndpointNameError(null);

        if (!endpointName) {
            setEndpointNameError('Length of the endpoint name should not be less than 5.');
            return;
        }

        if (endpointName.length < 5 || !endpointName) {
            setEndpointNameError('Length of the endpoint name should not be less than 5.');
        } else if (endpointName.length > 50) {
            setEndpointNameError('Length of the endpoint name should not be more than 50.');
        } else if (!/[a-z]/i.test(endpointName.charAt(0))) {
            setEndpointNameError('Endpoint name should start with an alphabet.');
        } else if (!/^[A-Za-z0-9]*$/.test(endpointName.charAt(endpointName.length - 1))) {
            setEndpointNameError('Endpoint name should end with an alphabet or a number.');
        } else if (!/^([A-Za-z0-9\s-])*$/.test(endpointName)) {
            setEndpointNameError('Endpoint name can only contain alphabet, number, space and hyphen.');
        } else {
            setEndpointNameError(null);
            addBorderColor(endpointNameInputRef);
        }
    };

    /**
     * To get the Endpoint Details from backend
     * @returns Promise<void>
     */
    const fetchEndPointDetails = async (): Promise<void> => {
        setComponentError(null);

        if (!endpointId) return;

        setLoading(true);

        try {
            const response: any = await getEndpointDetailsData(endpointId);
            if (response.success) {
                const endpointData = response.data;
                await getEndpointConsumption(endpointData.subscription_id);
                // Populate details
                const endpoint: EndointDetailsTypes = {
                    id: endpointData.id,
                    serviceId: endpointData.service_id,
                    endpointName: endpointData.name,
                    userId: endpointData.user_id,
                    workspaceId: endpointData.workspace_id,
                    apiKey: endpointData.api_key,
                    networkType: endpointData.networkType,
                    securityEnabled: endpointData.security ? endpointData.security.enabled : false,
                    jwtEnabled: endpointData.security.jwt ? endpointData.security.jwt.enabled : false,
                    jwtPublicName: endpointData.security.jwt ? endpointData.security.jwt.name : '',
                    jwtPublicKey: endpointData.security.jwt ? endpointData.security.jwt.public_key : '',
                    plan: endpointData.plan ? endpointData.plan : '',
                    addOns: endpointData.addOns ? endpointData.addOns : [],
                    subscription_id: endpointData.subscription_id,
                };

                /** set the states */
                setEndpointName(endpoint.endpointName);
                setupEndpointName(endpoint.endpointName);
                setEndpointData(endpoint);

                setEndpointURLs({
                    http: `${BaseUrls.getEndpointHttpUrl()}/${endpoint.apiKey}/`,
                    wss: `${BaseUrls.getEndpointWssUrl()}/${endpoint.apiKey}/`,
                });
                setupSecurity(endpoint);
            } else {
                setComponentError('Failed to get the endpoint details!');
            }
            // eslint-disable-next-line
        } catch (error) {
            setComponentError('Failed to get the endpoint details!');
        } finally {
            setLoading(() => false);
            toggleNameEditMode(false);
            toggleSecurityEditMode(false);
        }
    };

    /**
     * To update the Endpoint Name on backend
     * @returns Promise<void>
     *
     */
    const commitEndPointName = async (): Promise<void> => {
        setComponentError(null);

        try {
            const payload = {
                id: endpointData.id,
                name: endpointName,
            };
            const response: any = await updateEndpointName(payload, 'name');

            if (response) {
                if (response && response.success) {
                    removeBorderColorOnTimeout(endpointNameInputRef, 1000);

                    toggleModal(true);
                    setModalTitle('Endpoint Name');
                    setModalMessage('Endpoint name changed successfully');
                    toggleConfirmVisibility(false);
                    fetchEndPointDetails();
                    setComponentError(null);
                } else {
                    addBorderColor(endpointNameInputRef, 'red');
                    setEndpointNameError(response.message);
                }
            } else {
                addBorderColor(endpointNameInputRef, 'red');
                setEndpointNameError('Server Error...');
            }
            // eslint-disable-next-line
        } catch (error) {
            setComponentError('Failed to update the endpoint name!');
        } finally {
            setLoading(() => false);
            toggleNameEditMode(false);
        }
    };

    /**
     * To update the Endpoint Security Details on backend
     * @param payload Security payload
     * @returns Promise<void>
     *
     */
    const commitSecurityDetails = async (payload: any): Promise<void> => {
        setComponentError(null);

        try {
            payload.id = endpointData.id;

            const response: any = await updateSecurity(payload, 'security');
            if (response) {
                if (response && response.success) {
                    removeBorderColorOnTimeout(endpointNameInputRef, 1000);

                    toggleModal(true);
                    setModalTitle('Security details');
                    setModalMessage('Endpoint security updated successfully');
                    toggleConfirmVisibility(false);
                    fetchEndPointDetails();
                    setComponentError(null);
                } else {
                    setSecurityError(response.message);
                }
            } else {
                setSecurityError('Server Error...');
            }
            // eslint-disable-next-line
        } catch (error) {
            setComponentError('Failed to update the security details!');
        } finally {
            setLoading(() => false);
            toggleSecurityEditMode(false);
        }
    };

    /**
     * To Delete the Endpoint Details from backend
     * @returns Promise<void>
     *
     */
    // const deleteEndPointDetails = async (): Promise<void> => {
    //     try {
    //         const response: any = await deleteTheEndpoint(endpointData.id);
    //         if (response && response.success) {
    //             toggleModal(true);
    //             setModalTitle('Endpoint');
    //             setModalMessage('Endpoint deleted successfully');
    //             toggleConfirmVisibility(false);

    //             window.open(BaseUrls.getPlatformDashboardUrl(), '_self');
    //         } else {
    //             toggleModal(true);
    //             setModalTitle('Endpoint');
    //             setModalMessage('Error occured while deleting endpoint');
    //             toggleConfirmVisibility(false);
    //         }
    //     } catch (error) {
    //         console.error('deleteEndPointDetails error', error);
    //     } finally {
    //         setLoading(() => false);
    //     }
    // };

    /**
     * To get the Endpoint Consumption and Quota from backend
     * @returns Promise<void>
     *
     */
    const getEndpointConsumption = async (subscriptionId: string): Promise<void> => {
        try {
            const response: any = await getEndpointConsumed(subscriptionId);
            if (response && response.success) {
                setEndpointConsumed(response.data.consumption.apiUnits);
                setEndpointQuota(response.data.quota.apiUnits);
            } else {
                setEndpointConsumed('N/A');
                setEndpointQuota('N/A');
            }
            // eslint-disable-next-line
        } catch (error) {
            setComponentError('Failed to load the endpoint consumption');
        } finally {
            setLoading(() => false);
        }
    };

    // will run the following function on name change
    useEffect(() => {
        validateEndpointName();
    }, [endpointName]);

    /**Will run the code on component mount and update lifecycle */
    useEffect(() => {
        if (endpointId) {
            fetchEndPointDetails();
        }
    }, [endpointId]);

    // Will update the snippets on endpoint data change
    useEffect(() => {
        setCodes(() => {
            let httpURL = new URL('https://app.zeeve.io');
            let wsURL = new URL('wss://app.zeeve.io');
            try {
                httpURL = new URL(`${endpointURLs.http}cosmos/base/tendermint/v1beta1/node_info`);
                wsURL = new URL(`${endpointURLs.wss}websocket`);
            } catch (e) {
                httpURL = new URL('https://app.zeeve.io/shared-api/shardeum/unknown-url');
                wsURL = new URL('wss://app.zeeve.io/shared-api/shardeum/unknown-url/');
            }

            return [
                {
                    lang: 'CURL',
                    http: `
curl ${httpURL.href} -X GET -H 'Content-Type:application/json'`,
                },
                {
                    lang: 'WSCAT',
                    wss: `
npx wscat -c ${wsURL.href}`,
                },
                {
                    lang: 'NodeJs',
                    http: `
import axios from "axios";
                   
const config = {
  method: 'get',
  url: '${httpURL.href}',
  headers: { 
    'Content-Type': 'application/json'
  },
};
                    
axios(config)
.then(function (response) {
  console.log(JSON.stringify(response.data));
})
.catch(function (error) {
    console.log(error);
});`,
                    wss: `
import WebSocket from 'ws';

const ws = new WebSocket('${wsURL.href}');
                    
ws.on('open', function open() {
        console.log('connected');
                    
// NOTE : use delay to avoid missing messages from server
setTimeout(() => {
    const msg = {"jsonrpc":"2.0","method":"status","id":1};
    ws.send(JSON.stringify(msg));
    }, 1000);
});
                    
ws.on('message', function message(data) {
  console.log('received: %s', data);
});`,
                },
                {
                    lang: 'Python',
                    http: `
import requests

url = '${httpURL.href}'  

x = requests.get(url)

print(x.text)`,
                    wss: `
import asyncio
from websockets import connect
import time
import json

async def hello(uri):
        async with connect(uri) as websocket:
                print('connected')
                time.sleep(1)
                await websocket.send(json.dumps({"jsonrpc":"2.0","method":"status","id":1}))
                message = await websocket.recv()
                print(message)

asyncio.run(hello("${wsURL.href}"))`,
                },
                {
                    lang: 'Go',
                    http: `
package main

import (
    "fmt"
    "io/ioutil"
    "log"
    "net/http"
)
                     
func main() {

    resp, err := http.Get("${httpURL.href}")


   if err != nil {
       log.Fatalf("HTTP request error: %v", err)
   }
   defer resp.Body.Close()

   body, err := ioutil.ReadAll(resp.Body)
   if err != nil {
       log.Fatalf("ReadAll error: %v", err)
   }

   fmt.Println(string(body))
}`,
                    wss: `
package main

import (
	"log"
	"os"
	"os/signal"
	"time"

	"github.com/gorilla/websocket"
)

func main() {
	addr := "${wsURL.href}"
	interrupt := make(chan os.Signal, 1)
	signal.Notify(interrupt, os.Interrupt)

	log.Printf("connecting to %s", addr)
	c, _, err := websocket.DefaultDialer.Dial(addr, nil)
	if err != nil {
		log.Fatal("dial error:", err)
	}
	defer c.Close()

	done := make(chan struct{})
	go func() {
		defer close(done)
		for j := 1; j <= 1; j++ {
			_, message, err := c.ReadMessage()
			if err != nil {
				log.Println("read error:", err)
				return
			}
			log.Printf("received: %s", message)
		}
	}()

	ticker := time.NewTicker(time.Second)
	defer ticker.Stop()
	for {
		select {
		case <-done:
			log.Println("done")
			return
		case <-ticker.C:
			data := \`{ "jsonrpc": "2.0", "id": 1, "method": "status" }\`
			err = c.WriteMessage(websocket.TextMessage, []byte(data))
			if err != nil {
				log.Println("write error:", err)
				return
			}
			log.Printf("sent: %s", data)
		case <-interrupt:
			log.Println("interrupt")
			err := c.WriteMessage(websocket.CloseMessage, websocket.FormatCloseMessage(websocket.CloseNormalClosure, ""))
			if err != nil {
				log.Println("write close:", err)
				return
			}
			select {
			case <-done:
			case <-time.After(time.Second):
			}
			return
		}
	}
}`,
                },
            ];
        });
    }, [endpointURLs]);

    //Event Handlers [start]

    /**
     * Click handler for Endpoint Name Save Button
     */
    const onEndpointNameSave = () => {
        if (endpointNameError) {
            addBorderColor(endpointNameInputRef, 'red');
            setEndpointNameError('Uknown error occured. Please enter endpoint name again.');
            return;
        }

        if (endpointData.endpointName === endpointName) {
            addBorderColor(endpointNameInputRef, 'red');
            setEndpointNameError('Endpoint name can not be same as previous name.');
            toggleEPNameSavebtnDisable(true);
            return;
        }

        toggleEPNameSavebtnDisable(false);
        commitEndPointName();
    };

    /**
     * Click handler for Endpoint Delete Button
     */
    // const onDeleteEndpoint = (): void => {
    //     deleteEndPointDetails();
    // };

    /**
     * Input handler for Endpoint Name
     */
    const onEndpointNameChange = (value: string): void => {
        setEndpointName(value);
    };

    /**
     * Click handler for Security Save Button
     */
    const onSecuritySave = () => {
        let payload: any;

        payload = {
            id: endpointData.id,
            securityEnabled: false,
        };

        if (!isSecurity) {
            setSecurityType('none');
            resetSecurity();
            payload.securityEnabled = false;
            commitSecurityDetails(payload);
            return;
        }

        //JWT
        if (isSecurity && securityType === 'none') {
            setSecurityError('Please select any one security!');
            setTimeout(() => {
                setSecurityError(null);
            }, 2000);
            return;
        }

        if (isSecurity && securityType === 'JWT' && jwtSecurityRef.current) {
            if (!jwtSecurityRef.current.isNameValid()) {
                setSecurityError('Invalid JWT name!');
                setTimeout(() => {
                    setSecurityError(null);
                }, 2000);
                return;
            }

            if (!jwtSecurityRef.current.isKeyValid()) {
                setSecurityError('Invalid JWT public key!');
                setTimeout(() => {
                    setSecurityError(null);
                }, 2000);
                return;
            }

            setSecurityError(null);

            payload = {
                id: endpointData.id,
                securityEnabled: true,
                jwtEnabled: true,
                jwtName,
                jwtKey,
            };

            // const prevSec = {
            //     id: endpointData.id,
            //     securityEnabled: endpointData.securityEnabled,
            //     jwtEnabled: endpointData.jwtEnabled,
            //     jwtName: endpointData.jwtPublicName,
            //     jwtKey: endpointData.jwtPublicKey,
            // };

            // if (JSON.stringify(payload) !== JSON.stringify(prevSec)) commitSecurityDetails(payload);

            commitSecurityDetails(payload);
        }
    };
    //Event Handlers [end]

    return (
        <section className='my-4 position-relative'>
            {!isLoading && (
                <div>
                    <div className='my-4'>
                        <div className='d-flex justify-content-end text-end'></div>
                        <div className='row mb-3'>
                            <div className='col-sm-6 col-md-6 col-xl-6 col-lg-6 mx-auto'>
                                <label
                                    htmlFor='endpoint-name'
                                    className='col-auto col-form-label fw-bold'
                                    style={{ fontSize: '16px' }}
                                >
                                    Name
                                    <span className='text-danger'>*</span>
                                </label>
                                <div className='d-flex'>
                                    <input
                                        type='text'
                                        className='form-control'
                                        name='name'
                                        disabled={!isNameEditMode}
                                        defaultValue={endpointData.endpointName}
                                        value={endpointName}
                                        autoComplete='off'
                                        onChange={(e) => {
                                            onEndpointNameChange(e.target.value);
                                        }}
                                        ref={endpointNameInputRef}
                                    />
                                    &nbsp;&nbsp;&nbsp;&nbsp;
                                    {!isNameEditMode && (
                                        <Button
                                            type='button'
                                            className='btn btn-primary rounded'
                                            onClick={() => {
                                                toggleNameEditMode(true);
                                            }}
                                        >
                                            Edit
                                        </Button>
                                    )}
                                    {isNameEditMode && (
                                        <Button
                                            type='button'
                                            className='btn btn-secondary rounded'
                                            onClick={resetEndpointName}
                                        >
                                            Cancel
                                        </Button>
                                    )}
                                    {isNameEditMode && (
                                        <Button
                                            type='button'
                                            className='btn btn-success rounded ms-2'
                                            disabled={isEPNameSavebtnDisabled}
                                            onClick={onEndpointNameSave}
                                        >
                                            Save
                                        </Button>
                                    )}
                                </div>
                            </div>
                            <div className='col-sm-6 col-md-6 col-xl-6 col-lg-6 text-end pt-4'>
                                <div className='row'>
                                    <div className='col-8 fw-bold text-end' style={{ fontSize: '16px' }}>
                                        Network Type :-
                                    </div>
                                    <div className='col-4 text-start'>{endpointData.networkType}</div>
                                </div>
                                <div className='row'>
                                    <div className='col-8 fw-bold text-end'>Plan :-</div>
                                    <div className='col-4 text-start'>{endpointData.plan ? endpointData.plan : ''}</div>
                                </div>
                                <div className='row'>
                                    <div className='col-8 fw-bold text-end'>Quota :-</div>
                                    <div className='col-4 text-start'>{endpointQuota} API Units</div>
                                </div>
                                <div className='row'>
                                    <div className='col-8 fw-bold text-end'>Consumption :-</div>
                                    <div className='col-4 text-start'>{endpointConsumed} API Units</div>
                                </div>
                            </div>
                            {endpointNameError && (
                                <div>
                                    <p className='text-danger'>{endpointNameError}</p>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className='row'>
                        <div className='col-xl-6 col-lg-6 col-md-12 col-sm-12 mt-4'>
                            <div className='card py-4 px-4 border shadow-sm h-100  endpointcard'>
                                <div>
                                    <p className='text-center fw-bold' style={{ fontSize: '16px' }}>
                                        Endpoint
                                    </p>
                                    <p className='fw-bold mt-2 mb-2 d-flex justify-content-between'>HTTP</p>
                                    <div className='d-flex col align-items-center justify-content-between gap-2 border p-2 rounded-2 bg-light'>
                                        <p className='col-11 m-0' id='http'>
                                            {endpointURLs.http}
                                        </p>
                                        <i
                                            aria-hidden
                                            className='fa-regular fa-copy fs-6 cursor text-primary'
                                            onClick={() => {
                                                copyText(endpointURLs.http);
                                            }}
                                        />
                                    </div>
                                    <p className='fw-bold mt-2 mb-2 d-flex justify-content-between'>WS</p>
                                    <div className='d-flex col align-items-center justify-content-between gap-2 border p-2 rounded-2 bg-light'>
                                        <p className='col-11 m-0' id='wss'>
                                            {endpointURLs.wss}
                                        </p>
                                        <i
                                            aria-hidden
                                            className='fa-regular fa-copy fs-6 cursor text-primary'
                                            onClick={() => {
                                                copyText(endpointURLs.wss);
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className='col-xl-6 col-lg-6 col-md-12 col-sm-12 mt-4'>
                            <div className='card py-4 px-4 border shadow-sm h-100'>
                                <p className='text-center fw-bold' style={{ fontSize: '16px' }}>
                                    Example for making a request
                                </p>
                                <p className='text-end mt-2 mb-2'>
                                    <select
                                        className='form-select'
                                        defaultValue='Curl'
                                        onChange={(event) => {
                                            const selected = event.currentTarget.value as codeSnippetType;
                                            if (selected === 'WSCAT') setCodeType('wss');
                                            else setCodeType('https');
                                            setCodeSnippet(selected);
                                        }}
                                    >
                                        {codes.map((code, index) => {
                                            return (
                                                <option key={index} value={code.lang}>
                                                    {code.lang}
                                                </option>
                                            );
                                        })}
                                    </select>
                                </p>
                                <div className='card p-2 endpointcard rounded-2 bg-light'>
                                    <ul className='nav nav-tabs ' id='myTab' role='tablist'>
                                        <li
                                            className='nav-item'
                                            role='presentation'
                                            style={{
                                                display: codeSnippet === 'WSCAT' ? 'none' : 'block',
                                            }}
                                            onClick={() => setCodeType('https')}
                                        >
                                            <button
                                                className={`nav-link px-4 ${codeType === 'https' ? 'active' : ''}`}
                                                data-bs-toggle='tab'
                                                type='button'
                                                role='tab'
                                            >
                                                <span className='p-1'>HTTP</span>
                                            </button>
                                        </li>
                                        <li
                                            className='nav-item'
                                            role='presentation'
                                            style={{
                                                display: codeSnippet === 'CURL' ? 'none' : 'block',
                                            }}
                                            onClick={() => setCodeType('wss')}
                                        >
                                            <button
                                                className={`nav-link px-4 ${codeType === 'wss' ? 'active' : ''}`}
                                                data-bs-toggle='tab'
                                                type='button'
                                                role='tab'
                                            >
                                                <span className='p-1'>WS</span>
                                            </button>
                                        </li>
                                    </ul>
                                    <div className='d-flex justify-content-between gap-2'>
                                        <pre>
                                            <code className=''>
                                                {codeType === 'https' &&
                                                    codes.find((code) => codeSnippet === code.lang)?.http}
                                                {codeType === 'wss' &&
                                                    codes.find((code) => codeSnippet === code.lang)?.wss}
                                            </code>
                                        </pre>
                                        <span>
                                            <i
                                                aria-hidden
                                                className='fa-regular fa-copy fs-6 cursor text-primary p-2'
                                                onClick={() => {
                                                    let content: string | undefined = '';

                                                    if (codeType === 'https')
                                                        content = codes.find((code) => codeSnippet === code.lang)?.http;
                                                    else content = codes.find((code) => codeSnippet === code.lang)?.wss;

                                                    if (content) copyText(content);
                                                }}
                                            />
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        {isCopy && (
                            <div style={{ width: '50%' }} className='alert alert-success mt-3 mb-5' role='alert'>
                                Copied
                            </div>
                        )}
                    </div>
                    <div className='my-5'>
                        <div className='d-flex align-items-center justify-content-between col-sm-6 pe-1'>
                            <div className='form-check form-switch '>
                                <input
                                    className='form-check-input'
                                    type='checkbox'
                                    id='security'
                                    disabled={!isSecurityEditMode}
                                    checked={isSecurity}
                                    onChange={() => {
                                        toggleSecurity((prev) => {
                                            return !prev;
                                        });
                                    }}
                                />

                                <label className='form-check-label fw-bold' htmlFor='security'>
                                    SECURITY
                                </label>
                            </div>
                            <div>
                                {!isSecurityEditMode && (
                                    <Button
                                        type='button'
                                        className='btn btn-primary rounded me-2'
                                        onClick={() => {
                                            toggleSecurityEditMode(true);
                                        }}
                                    >
                                        Edit
                                    </Button>
                                )}

                                {isSecurityEditMode && (
                                    <Button
                                        type='button'
                                        className='btn btn-secondary rounded me-2'
                                        onClick={resetSecurity}
                                    >
                                        Cancel
                                    </Button>
                                )}

                                {isSecurityEditMode && (
                                    <Button
                                        type='button'
                                        className='btn btn-success rounded me-2'
                                        onClick={onSecuritySave}
                                    >
                                        Save
                                    </Button>
                                )}
                            </div>
                        </div>

                        <>
                            <h6 className='my-4'>JWT</h6>
                            <div className='col-xl-4 col-lg-5 col-sm-8 col-md-8'>
                                <div className='form-check mb-3 editinput' id='editinput3'>
                                    <Inputbox
                                        className='form-check-input'
                                        type='checkbox'
                                        name='jwt_enable'
                                        id='require-jwt'
                                        disabled={!isSecurityEditMode || !isSecurity}
                                        checked={securityType === 'JWT' && isSecurity}
                                        onChange={(value: any) => {
                                            if (!isSecurity) return;

                                            if (value.target.checked) setSecurityType('JWT');
                                            else setSecurityType('none');
                                        }}
                                    />
                                    <label className='form-check-label ms-2' htmlFor='require-jwt'>
                                        Require JWT
                                    </label>
                                    <i
                                        title='Restrict API usage to requests that include a valid JWT'
                                        className='fa-solid fa-circle-info ms-1'
                                    />
                                </div>
                            </div>

                            <JWTSecurity
                                ref={jwtSecurityRef}
                                isEditMode={isSecurityEditMode && isSecurity && securityType === 'JWT'}
                                jwtKey={jwtKey}
                                jwtName={jwtName}
                                setJWTKey={setJWTKey}
                                setJWTName={setJWTName}
                            />
                        </>

                        {securityError && (
                            <div className='alert-danger mt-4 rounded text-center'>
                                <p className='text-danger  py-2'>{securityError}</p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {isLoading && <Loader />}
            {componentError && (
                <div className='alert-danger mt-4 rounded text-center'>
                    <p className='text-danger  py-2'>{componentError}</p>
                </div>
            )}

            <Modal
                show={showModal}
                onHide={() => {
                    toggleModal(false);
                    setModalTitle('');
                    setModalMessage('');
                }}
                title={modalTitle}
                visibleFooter
                visibleHeaderCloseIcon={false}
                visibleFooterCloseButton={modalVisibleCancelBtn}
                visibleConfirmBtn={modalVisibleConfirmBtn}
                confirmBtnText={modalConfirmText}
                confirmBtnClassName=''
            >
                <p className='status text-center mt-3'>{modalMessage}</p>
            </Modal>
        </section>
    );
});

Basic.displayName = 'Basic';

export default Basic;
